import { ReactElement, useEffect, useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import SectionHeader from '../../components/SectionHeader';
import Table from '../../components/Table';
import { useUser } from '../../hooks/useUser';
import { useAdminActivity } from '../../hooks/useAdminActivity';
import type { AdminActivitySession } from '../../api/adminActivity';
import { selectClass } from '../../components/formControlClasses';

const DAY_OPTIONS = [7, 30, 90, 365];

/**
 * A plain `YYYY-MM-DD` handed to `new Date()` is read as UTC midnight, which
 * renders as the previous day for anyone west of Greenwich. These are calendar
 * dates with no time in them, so reformat the parts directly.
 */
function formatDate(value: string | null): string {
  if (!value) return '—';
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

/** `last_run_ts` is a real instant, so the usual locale formatting applies. */
function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

interface Warning {
  tone: 'error' | 'warning';
  label: string;
}

/**
 * Roll a session's counts up into the badges shown in the Warnings column.
 *
 * Failed runs and unhealthy runs are separate signals — a run can process
 * cleanly and still carry an ERROR health verdict, and a run that never
 * processed carries no health verdict at all. Both are worth an admin's
 * attention, so neither is folded into the other.
 */
function warningsFor(row: AdminActivitySession): Warning[] {
  const warnings: Warning[] = [];
  if (row.failed_runs > 0) {
    warnings.push({ tone: 'error', label: `${row.failed_runs} failed` });
  }
  if (row.error_runs > 0) {
    warnings.push({ tone: 'error', label: `${row.error_runs} run error` });
  }
  if (row.warning_runs > 0) {
    warnings.push({ tone: 'warning', label: `${row.warning_runs} run warning` });
  }
  if (row.autocal_failed) {
    warnings.push({ tone: 'warning', label: 'autocal failed' });
  }
  return warnings;
}

function WarningBadges({ row }: { row: AdminActivitySession }): ReactElement {
  const warnings = warningsFor(row);
  if (warnings.length === 0) {
    return <span className="cr-text-gray-400">—</span>;
  }
  return (
    <div className="cr-flex cr-flex-wrap cr-gap-1">
      {warnings.map((warning) => (
        <span
          key={warning.label}
          className={
            'cr-whitespace-nowrap cr-rounded-full cr-px-2 cr-py-0.5 cr-text-xs cr-font-medium ' +
            (warning.tone === 'error'
              ? 'cr-bg-red-100 cr-text-red-800'
              : 'cr-bg-amber-100 cr-text-amber-800')
          }
        >
          {warning.label}
        </span>
      ))}
    </div>
  );
}

function SummaryTile({ label, value }: { label: string; value: number | string }): ReactElement {
  return (
    <div className="cr-flex cr-flex-col cr-rounded-lg cr-bg-white cr-px-4 cr-py-2 cr-shadow-md">
      <span className="cr-text-xl cr-font-semibold cr-text-blue-900">{value}</span>
      <span className="cr-text-xs cr-text-gray-500">{label}</span>
    </div>
  );
}

/**
 * Installation-wide activity for the last N days (OMNISCIENT only — both the
 * route userFilter and the endpoint itself enforce that).
 *
 * Opening a session only works for the team the admin is currently switched
 * to — every session page is team-scoped and would 404 otherwise. Rows from
 * other teams say so rather than navigating into a dead end; switching team
 * is done on the Teams page.
 */
export default function AdminActivity(): ReactElement {
  const [days, setDays] = useState<number>(30);
  const [teamFilter, setTeamFilter] = useState<string>('');
  const [warningsOnly, setWarningsOnly] = useState<boolean>(false);
  const [userProfile] = useUser();
  const navigate = useNavigate();

  const { data, isPending, error } = useAdminActivity(days);

  const sessions = useMemo(() => data?.sessions ?? [], [data]);

  const teams = useMemo(() => {
    const byId = new Map<string, string>();
    sessions.forEach((session) => byId.set(session.team_id, session.team_name));
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [sessions]);

  const rows = useMemo(
    () =>
      sessions
        .filter((session) => !teamFilter || session.team_id === teamFilter)
        .filter((session) => !warningsOnly || session.has_warning),
    [sessions, teamFilter, warningsOnly],
  );

  const columns = useMemo(() => {
    const helper = createColumnHelper<AdminActivitySession>();
    return [
      helper.accessor('activity_date', {
        header: 'Date',
        cell: (info) => (
          <span className={info.row.original.date ? '' : 'cr-text-gray-500 cr-italic'}>
            {formatDate(info.getValue())}
          </span>
        ),
      }),
      helper.accessor('team_name', { header: 'Team' }),
      helper.accessor('description', {
        header: 'Session',
        cell: (info) => (
          <span className={info.row.original.hidden ? 'cr-text-gray-400 cr-line-through' : ''}>
            {info.getValue()}
          </span>
        ),
      }),
      helper.accessor('run_count', { header: 'Runs' }),
      helper.display({
        id: 'warnings',
        header: 'Warnings',
        cell: (info) => <WarningBadges row={info.row.original} />,
      }),
      helper.accessor('last_run_ts', {
        header: 'Last run',
        cell: (info) => formatTimestamp(info.getValue()),
      }),
    ];
  }, []);

  useEffect(() => {
    if (error) toast.error('Could not load activity');
  }, [error]);

  const totals = data?.totals;

  return (
    <div className="cr-flex cr-flex-col cr-gap-4">
      <SectionHeader title="Activity" />

      <div className="cr-flex cr-flex-wrap cr-gap-3">
        <SummaryTile label="Sessions" value={totals?.session_count ?? 0} />
        <SummaryTile label="Runs" value={totals?.run_count ?? 0} />
        <SummaryTile label="With warnings" value={totals?.warning_session_count ?? 0} />
        <SummaryTile label="Teams active" value={totals?.team_count ?? 0} />
      </div>

      <div className="cr-flex cr-flex-wrap cr-items-end cr-gap-3">
        <label className="cr-flex cr-flex-col cr-text-sm cr-font-semibold cr-text-blue-900">
          Window
          <select className={selectClass} value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
            {DAY_OPTIONS.map((option) => (
              <option key={option} value={String(option)}>
                Last {option} days
              </option>
            ))}
          </select>
        </label>
        <label className="cr-flex cr-flex-col cr-text-sm cr-font-semibold cr-text-blue-900">
          Team
          <select className={selectClass} value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}>
            <option value="">All teams</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </label>
        <label className="cr-flex cr-items-center cr-gap-2 cr-text-sm cr-font-semibold cr-text-blue-900">
          <input
            type="checkbox"
            checked={warningsOnly}
            onChange={(e) => setWarningsOnly(e.target.checked)}
          />
          Warnings only
        </label>
      </div>

      <Table
        data={rows}
        columns={columns}
        isLoading={isPending}
        getRowId={(row) => row.id}
        rowClassName={(row) => (row.has_warning ? 'cr-bg-amber-50' : '')}
        onRowClick={(_event, row) => {
          if (row.team_id !== userProfile.team_id) {
            toast(`${row.team_name} is not your current team — switch to it on the Teams page to open this session.`);
            return;
          }
          navigate(`/sessions/${row.id}`);
        }}
      />
    </div>
  );
}
